import { useState, useMemo, useRef, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { getFlagImageUrl } from '../utils/flags';
import type { Country } from '../types';

// Flag image component with fallback - rectangular to show full flag respectfully
function FlagImage({ code, size = 18, style }: { code: string; size?: number; style?: CSSProperties }) {
  const [error, setError] = useState(false);
  const flagWidth = size * 1.5; // 3:2 aspect ratio
  const flagHeight = size;
  
  if (error) {
    return (
      <span style={{ fontSize: size * 0.5, fontWeight: 600, color: 'white', ...style }}>
        {code}
      </span>
    );
  }
  
  return (
    <img 
      src={getFlagImageUrl(code)} 
      alt={code}
      onError={() => setError(true)}
      style={{ 
        width: flagWidth, 
        height: flagHeight, 
        objectFit: 'cover', 
        borderRadius: '3px',
        ...style 
      }}
    />
  );
}

interface CountrySelectorProps {
  countries: Country[];
  selectedCode: string | null;
  onChange: (code: string | null) => void;
  disabled?: boolean;
}

export default function CountrySelector({
  countries,
  selectedCode,
  onChange,
  disabled = false,
}: CountrySelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter countries based on search
  const filteredCountries = useMemo(() => {
    if (!searchQuery) return countries;
    const query = searchQuery.toLowerCase();
    return countries.filter(c => 
      c.name.toLowerCase().includes(query) || 
      c.code.toLowerCase().includes(query)
    );
  }, [countries, searchQuery]);

  // Get selected country
  const selectedCountry = useMemo(() => {
    if (!selectedCode) return null;
    return countries.find(c => c.code === selectedCode) || null;
  }, [countries, selectedCode]);

  const handleSelect = (code: string | null) => {
    onChange(code);
    setIsOpen(false);
    setSearchQuery('');
  };

  const containerStyle: CSSProperties = {
    position: 'relative',
    width: '100%',
  };

  const buttonStyle: CSSProperties = {
    width: '100%',
    padding: '12px 14px',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px',
    color: selectedCountry ? 'white' : 'rgba(255,255,255,0.4)',
    fontSize: '14px',
    textAlign: 'left',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    opacity: disabled ? 0.5 : 1,
  };

  const dropdownStyle: CSSProperties = {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    maxHeight: '280px',
    overflowY: 'auto',
    background: 'rgba(10, 10, 20, 0.98)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px',
    zIndex: 100,
    boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
  };

  const searchStyle: CSSProperties = {
    width: '100%',
    padding: '12px 14px',
    background: 'transparent',
    border: 'none',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    color: 'white',
    fontSize: '14px',
    outline: 'none',
  };

  const optionStyle = (isSelected: boolean): CSSProperties => ({
    padding: '10px 14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: isSelected ? 'rgba(20, 241, 149, 0.1)' : 'transparent',
    borderLeft: isSelected ? '3px solid #14F195' : '3px solid transparent',
    transition: 'background 0.15s',
  });

  return (
    <div ref={dropdownRef} style={containerStyle}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={buttonStyle}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {selectedCountry ? (
            <>
              <FlagImage code={selectedCountry.code} size={18} />
              <span>{selectedCountry.name}</span>
            </>
          ) : (
            <span>Select your country...</span>
          )}
        </span>
        <svg 
          style={{ 
            width: '16px', 
            height: '16px', 
            color: 'rgba(255,255,255,0.4)',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div style={dropdownStyle} className="custom-scrollbar">
          <input
            ref={inputRef}
            type="text"
            placeholder="Search countries..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={searchStyle}
            autoFocus
          />
          
          {/* Clear option */}
          {selectedCode && (
            <div
              onClick={() => handleSelect(null)}
              style={{
                ...optionStyle(false),
                color: 'rgba(255,255,255,0.5)',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <span style={{ fontSize: '16px' }}>❌</span>
              <span>Clear selection</span>
            </div>
          )}

          {/* Country options */}
          {filteredCountries.length > 0 ? (
            filteredCountries.map((country) => (
              <div
                key={country.code}
                onClick={() => handleSelect(country.code)}
                style={optionStyle(selectedCode === country.code)}
                onMouseEnter={(e) => {
                  if (selectedCode !== country.code) {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedCode !== country.code) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <FlagImage code={country.code} size={18} />
                <span style={{ flex: 1, color: 'white', fontSize: '14px' }}>{country.name}</span>
                {country.memberCount > 0 && (
                  <span style={{
                    fontSize: '10px',
                    color: '#14F195',
                    background: 'rgba(20,241,149,0.15)',
                    padding: '2px 6px',
                    borderRadius: '4px',
                  }}>
                    {country.memberCount} 👥
                  </span>
                )}
              </div>
            ))
          ) : (
            <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
              No countries found
            </div>
          )}
        </div>
      )}
    </div>
  );
}
